import { useState, useCallback } from 'react';
import { CalendarDays, ChevronDown, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

/* ── helpers ─────────────────────────────────────────────────────────── */
const fmt = (d: Date) => d.toISOString().slice(0, 10);

const today      = () => { const d = new Date(); return fmt(d); };
const yesterday  = () => { const d = new Date(); d.setDate(d.getDate() - 1); return fmt(d); };
const daysAgo    = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return fmt(d); };
const monthStart = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() - offset, 1);
  return fmt(d);
};
const monthEnd   = (offset = 0) => {
  const d = new Date();
  if (offset === 0) return today();
  d.setMonth(d.getMonth() - offset + 1, 0);
  return fmt(d);
};

/* ── preset definitions ──────────────────────────────────────────────── */
interface Preset { label: string; from: () => string; to: () => string; }

const PRESETS: Preset[] = [
  { label: 'Today',          from: today,               to: today },
  { label: 'Yesterday',      from: yesterday,            to: yesterday },
  { label: 'Last 7 days',    from: () => daysAgo(6),    to: today },
  { label: 'Prior week',     from: () => daysAgo(14),   to: () => daysAgo(7) },
  { label: 'Last 14 days',   from: () => daysAgo(13),   to: today },
  { label: 'Last 30 days',   from: () => daysAgo(29),   to: today },
  { label: 'This month',     from: () => monthStart(0), to: () => monthEnd(0) },
  { label: 'Last month',     from: () => monthStart(1), to: () => monthEnd(1) },
];

/* ── component ───────────────────────────────────────────────────────── */
interface DateRangeFilterProps {
  fromDate: string;
  toDate: string;
  onFromChange: (d: string) => void;
  onToChange: (d: string) => void;
  onApply: () => void;
  loading?: boolean;
  lastUpdated?: Date;
  initialPreset?: string;
}

export function DateRangeFilter({
  fromDate, toDate,
  onFromChange, onToChange,
  onApply, loading, lastUpdated,
  initialPreset = 'Last 30 days',
}: DateRangeFilterProps) {
  const [activePreset, setActivePreset] = useState<string>(initialPreset);
  const [open, setOpen] = useState(false);

  const applyPreset = useCallback((preset: Preset) => {
    const f = preset.from();
    const t = preset.to();
    onFromChange(f);
    onToChange(t);
    setActivePreset(preset.label);
    // Do NOT auto-fetch — user must click "Apply Range" to confirm
  }, [onFromChange, onToChange]);

  const handleCustomChange = (field: 'from' | 'to', val: string) => {
    if (field === 'from') onFromChange(val);
    else onToChange(val);
    setActivePreset('Custom');
  };

  // Display label for the trigger
  const displayLabel = activePreset === 'Custom'
    ? `${fromDate} → ${toDate}`
    : activePreset;

  return (
    <div className="relative">
      {/* ── Trigger button ────────────────────────────── */}
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          'flex h-8 items-center gap-2 rounded-lg border px-3 text-xs font-medium transition-all',
          'border-border bg-background text-foreground',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          open && 'bg-accent ring-2 ring-ring ring-offset-1'
        )}
      >
        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="max-w-[160px] truncate">{displayLabel}</span>
        <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {/* ── Dropdown panel ────────────────────────────── */}
      {open && (
        <>
          {/* Click-away overlay */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />

          <div className={cn(
            'absolute right-0 top-[calc(100%+6px)] z-40 w-72',
            'rounded-xl border border-border bg-popover shadow-dropdown',
            'animate-fade-in',
          )}>
            {/* Quick presets */}
            <div className="p-3 border-b border-border">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Quick Select
              </p>
              <div className="grid grid-cols-2 gap-1">
                {PRESETS.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => { applyPreset(preset); /* stay open so user can click Apply */ }}
                    className={cn(
                      'rounded-md px-2.5 py-1.5 text-left text-xs transition-all',
                      activePreset === preset.label
                        ? 'bg-primary text-primary-foreground font-semibold'
                        : 'text-foreground hover:bg-accent'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom range */}
            <div className="p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Custom Range
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">From</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={e => handleCustomChange('from', e.target.value)}
                    className={cn(
                      'w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                      'text-foreground',
                    )}
                  />
                </div>
                <div className="mt-4 text-muted-foreground text-xs">→</div>
                <div className="flex-1">
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">To</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={e => handleCustomChange('to', e.target.value)}
                    className={cn(
                      'w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                      'text-foreground',
                    )}
                  />
                </div>
              </div>

              <Button
                size="sm"
                className="mt-3 w-full h-8 text-xs"
                onClick={() => { onApply(); setOpen(false); }}
                disabled={loading}
              >
                {loading
                  ? <><RefreshCw className="h-3 w-3 animate-spin" /> Applying…</>
                  : 'Apply Range'
                }
              </Button>
            </div>

            {/* Last updated */}
            {lastUpdated && (
              <div className="border-t border-border px-3 py-2">
                <p className="text-[10px] text-muted-foreground">
                  Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
